import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useCustomerAuth } from '@/components/customer/CustomerAuthProvider';

interface OrganizationBranding {
  name: string;
  shortName?: string;
  logoUrl?: string;
  faviconUrl?: string;
  brandColorHex: string;
  defaultPortalLanguage: string;
  isLoading: boolean;
  error?: string;
}

// Convert hex to HSL format
function hexToHsl(hex: string): string {
  // Remove the hash if it exists
  hex = hex.replace('#', '');
  
  // Convert to RGB
  const r = parseInt(hex.substr(0, 2), 16) / 255;
  const g = parseInt(hex.substr(2, 2), 16) / 255;
  const b = parseInt(hex.substr(4, 2), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

export function useOrganizationBranding(): OrganizationBranding {
  const { customer } = useCustomerAuth();
  const [branding, setBranding] = useState<OrganizationBranding>({
    name: 'Sposa Suite',
    brandColorHex: '#EC4899',
    defaultPortalLanguage: 'en',
    isLoading: true
  });

  // Apply CSS variables immediately with default values
  const applyCSSVariables = (colorHex: string) => {
    try {
      const hslColor = hexToHsl(colorHex);
      const root = document.documentElement;
      
      console.log('Applying CSS variables:', { colorHex, hslColor });
      
      root.style.setProperty('--primary', hslColor);
      root.style.setProperty('--brand-color', colorHex);
      
      return true;
    } catch (error) {
      console.error('Failed to apply CSS variables:', error);
      return false;
    }
  };

  // Apply default CSS variables immediately on mount
  useEffect(() => {
    applyCSSVariables('#EC4899');
  }, []);

  useEffect(() => {
    const fetchBranding = async (retryCount = 0) => {
      if (!customer?.organization_id) {
        setBranding(prev => ({ ...prev, isLoading: false }));
        return;
      }

      try {
        console.log('Fetching branding via edge function for org:', customer.organization_id);
        
        const { data: response, error } = await supabase.functions.invoke('customer-organization-branding', {
          body: {
            organization_id: customer.organization_id,
            include_css: true
          }
        });

        if (error) {
          console.error('Error calling branding function:', error);
          
          // Retry on CORS or network errors
          if (retryCount < 2 && (error.message?.includes('CORS') || error.message?.includes('network') || error.message?.includes('fetch'))) {
            console.log(`Retrying branding fetch (attempt ${retryCount + 1})`);
            setTimeout(() => fetchBranding(retryCount + 1), 1000 * (retryCount + 1));
            return;
          }
          
          setBranding(prev => ({ 
            ...prev, 
            isLoading: false, 
            error: 'Failed to load branding' 
          }));
          return;
        }

        const brandingData = response.data;
        console.log('Received branding data:', brandingData);

        const newBranding = {
          name: brandingData.name,
          shortName: brandingData.shortName,
          logoUrl: brandingData.logoUrl,
          faviconUrl: brandingData.faviconUrl,
          brandColorHex: brandingData.brandColorHex,
          defaultPortalLanguage: brandingData.defaultPortalLanguage,
          isLoading: false
        };

        setBranding(newBranding);

        // Apply CSS variables with the new brand color
        if (brandingData.brandColorHex) {
          applyCSSVariables(brandingData.brandColorHex);
        } else if (brandingData.cssVariables) {
          // Fallback to server-processed values if available
          const root = document.documentElement;
          root.style.setProperty('--primary', brandingData.cssVariables.primary);
          root.style.setProperty('--brand-color', brandingData.cssVariables.brandColor);
        }

      } catch (error) {
        console.error('Error fetching organization branding:', error);
        
        // Retry on fetch errors
        if (retryCount < 2) {
          console.log(`Retrying branding fetch due to error (attempt ${retryCount + 1})`);
          setTimeout(() => fetchBranding(retryCount + 1), 1000 * (retryCount + 1));
          return;
        }
        
        setBranding(prev => ({ 
          ...prev, 
          isLoading: false, 
          error: 'Failed to load branding' 
        }));
      }
    };

    fetchBranding();
  }, [customer?.organization_id]);

  return branding;
}